import React, { FC, useEffect } from "react";

// UI
import { Box } from "@UI/Components/Box/Box";

// Hooks
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";

// payments
import { PaymentMethodType } from "@modules/PaymentProcessing/types";

// components
import { IOutlets } from "@Libs/Transclusion/types/outlets";
import { PaymentFlowLayout } from "../../../Components/Layouts/PaymentFlowLayout/PaymentFlowLayout";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";
import { createOrUpdatePaymentMethod } from "@modules/PaymentProcessing/services/paymentMethods";
import { useSavePaymentPreferencesFlow } from "@modules/PaymentProcessing/hooks/useSavePaymentPreferencesFlow";
import { ConfigureBancontact } from "@modules/PaymentProcessing/modules/Bancontact/Components/ConfigureBancontact/ConfigureBancontact";
import { ConfigureCreditCard } from "@modules/PaymentProcessing/modules/CreditCard/Components/ConfigureCreditCard/ConfigureCreditCard";
import { ConfigureBankTransfer } from "@modules/PaymentProcessing/modules/BankTransfer/Components/ConfigureBankTransfer/ConfigureBankTransfer";
import { ConfigurePayPal } from "@modules/PaymentProcessing/modules/PayPal/Components/ConfigurePayPal/ConfigurePayPal";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";

export const ConfigurePaymentMethod: FC = () => {
  const {
    state: {
      allowedPaymentMethods,
      enabledPaymentMethods,
      operationSummary,
      paymentFormInfo,
    },
    onSuccess,
    onPageMount,
    navigateToStep,
    rootRoute,
  } = useSavePaymentPreferencesFlow();
  const navigate = useNavigate();

  const paymentMethod = useSearchParams("paymentMethod") as PaymentMethodType;

  useEffect(() => {
    onPageMount?.("CONFIGURE_PAYMENT_METHOD");
  }, []);

  useEffect(() => {
    if (
      !(
        allowedPaymentMethods.includes(paymentMethod) &&
        enabledPaymentMethods.includes(paymentMethod)
      )
    ) {
      navigateToStep("CHOOSE_PAYMENT_METHOD");
    }
  }, []);

  const handleSubmitPaymentMethodConfig: IOutlets["configure-preferred-payment-method.body"]["props"]["onSubmit"] =
    async ({ paymentMethod, provider }, callbacks) => {
      try {
        const response = await createOrUpdatePaymentMethod({
          method: paymentMethod,
          provider,
          preferred: true,
        });
        callbacks?.onSuccess && (await callbacks.onSuccess());
        onSuccess?.({ paymentMethod, provider });
        return response;
      } catch (error) {
        callbacks?.onError && (await callbacks.onError(error));
        throw error;
      }
    };

  const handleBack = () => {
    navigate(rootRoute.to, rootRoute);
  };

  return (
    <PaymentFlowLayout>
      <PaymentFlowLayout.OperationSummary>
        {operationSummary}
      </PaymentFlowLayout.OperationSummary>
      <PaymentFlowLayout.Header>
        <FormattedMessage {...messages.choosePaymentMethodAndPayService} />
      </PaymentFlowLayout.Header>
      <PaymentFlowLayout.Content>
        <Box w="full" alignItems="center" justifyContent="center">
          <Box
            shadow="lg"
            bg="white"
            p={[5, 5, 10]}
            borderRadius="xl"
            alignContent="center"
            data-testid="configure-payment-method"
          >
            {paymentMethod == PaymentMethodType.CREDIT_CARD && (
              <ConfigureCreditCard
                onBack={handleBack}
                onSubmit={handleSubmitPaymentMethodConfig}
              />
            )}
            {paymentMethod == PaymentMethodType.PAYPAL && (
              <ConfigurePayPal
                onBack={handleBack}
                onSubmit={handleSubmitPaymentMethodConfig}
              />
            )}
            {paymentMethod == PaymentMethodType.BANCONTACT && (
              <ConfigureBancontact
                onBack={handleBack}
                onSubmit={handleSubmitPaymentMethodConfig}
              />
            )}
            {paymentMethod == PaymentMethodType.BANK_TRANSFER && (
              <ConfigureBankTransfer
                onBack={handleBack}
                onSubmit={handleSubmitPaymentMethodConfig}
              />
            )}
          </Box>
        </Box>
        <Box mt="5">{paymentFormInfo}</Box>
      </PaymentFlowLayout.Content>
    </PaymentFlowLayout>
  );
};
